import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { frFR } from '@mui/material/locale';
import Modal from 'react-bootstrap/Modal';
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import { Box, IconButton } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import { ImageCategory, selectBestImage } from "../utils/imageUtils";


import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';

import { OuvrageDetails } from "./OuvrageDetails";
import { Ouvrage } from "../types";
import { deadSmile, greenSmile, pokerFace, redSmile } from "../const";
import { useSelector } from "react-redux";
import { State } from "../reducer";
import {
    refreshCycladeOuvrageList,
    refreshElibOuvrageList,
    refreshOuvrageList,
    setCount
} from "../action/ouvrageAction";
import { formatDate } from "../utils/datFormat";
import { ModalImage } from "./ModalImage";
import { ModalComplementImage } from "./ModalComplementImage";
import { getElibs } from "../action/elibAction";
import { OuvrageCount } from "./OuvragesCount";
import { getTitleWithRevInfo } from "../utils/ouvrageUtils";
import { useAppDispatch } from "../store";
import { deepmerge } from "@mui/utils";
import { theme as baseTheme } from "../theme";

const theme = createTheme(deepmerge(baseTheme, {
    components: {
        MuiTableCell: {
            styleOverrides: {
                footer: { borderBottom: 'none' },
                root: {
                    fontSize: '0.775rem',
                    lineHeight: 1.23,
                    padding: '8px',
                },
                head: { lineHeight: 1.3 }
            }
        }
    }
}), frFR);

const OuvrageTable: React.FC = () => {
    const dispatch = useAppDispatch();
    const ouvrageDetailsRef = React.useRef<any>(null);

    const ouvrageList = useSelector(({ ouvrages }: State) => ouvrages.filterOuvrages);
    const loading = useSelector(({ ouvrages }: State) => ouvrages.ouvrageLoading);

    const [show, setShow] = useState(false);
    const [img, ] = useState("");
    const handleClose = () => setShow(false);

    useEffect(() => {
        dispatch(getElibs());
    }, [dispatch]);

    const getSmile = (value: string) => {
        switch (value) {
            case 'Non valide': return redSmile;
            case 'Valide': return greenSmile;
            case 'Non vérifié': return deadSmile;
            case 'Inconnu': return pokerFace;
            default: return pokerFace;
        }
    };

    const handleClickForModal = (ouvrage: Ouvrage) => {
        ouvrageDetailsRef.current?.updateShowDetails(true);
        ouvrageDetailsRef.current?.updateSelectedOuvrage(ouvrage);
    };

    // Actions Redux
    const refreshList = (list: Ouvrage[] | Ouvrage) => {
        dispatch(refreshOuvrageList(Array.isArray(list) ? list.map(x => x.ean) : [list.ean]));
    };
    const checkList = (list: Ouvrage[] | Ouvrage) => {
        dispatch(refreshCycladeOuvrageList(Array.isArray(list) ? list.map(x => x.ean) : [list.ean]));
    };
    const checkElibList = (list: Ouvrage[] | Ouvrage) => {
        dispatch(refreshElibOuvrageList(Array.isArray(list) ? list.map(x => x.ean) : [list.ean]));
    };

    // 1. Définition des colonnes (Memoïsée pour les performances de MRT)
    const columns = useMemo<MRT_ColumnDef<Ouvrage>[]>(() => [
        {
            accessorKey: 'cover',
            header: 'Couv',
            enableSorting: false,
            Cell: ({ row }) => {
                const cover     = selectBestImage(row.original.images, ImageCategory.COVER);
                const backCover = selectBestImage(row.original.images, ImageCategory.BACK_COVER);
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <ModalImage cover={cover?.url || ""} isVisuelProvisoire={cover?.isVisuelProvisoire} />
                        {backCover && (
                            <span> <ModalImage cover={backCover.url} isVisuelProvisoire={backCover.isVisuelProvisoire} /></span>
                        )}
                    </Box>
                );
            },
        },
        {
            accessorKey: 'fluxLibrairie',
            header: 'Flux',
            size: 40,
            Cell: ({ row }) => (
                <Box sx={{ textAlign: 'center' }}>
                    <Checkbox color="primary" checked={row.original.fluxLibrairie} size="small" disabled />
                </Box>
            ),
        },
        {
            accessorKey: 'title',
            header: 'Titre',
            sortingFn: (rowA, rowB) => rowA.original.title.localeCompare(rowB.original.title),
            Cell: ({ row }) => (
                <label style={{ width: '12rem', cursor: 'pointer' }} onClick={() => handleClickForModal(row.original)}>
                    {getTitleWithRevInfo(row.original)}
                </label>
            ),
        },
        {
            accessorKey: 'complement',
            header: 'Complément',
            Cell: ({ row }) => {
                const complements = row.original.complement 
                    ? [...row.original.complement].sort((a, b) => a.name > b.name ? 1 : -1) 
                    : [];
                return (
                    <Box sx={{ width: '12rem', textAlign: 'center' }}>
                        {complements.map(comp => (
                            <ModalComplementImage key={'modal_' + comp.id} complement={comp} />
                        ))}
                    </Box>
                );
            }
        },
        { accessorKey: 'ean', header: 'EAN' },
        {
            accessorKey: 'mevDate',
            header: 'MEV',
            Cell: ({ cell }) => <label>{formatDate(new Date(cell.getValue<string>()))}</label>
        },
        {
            accessorKey: 'run',
            header: 'RUN',
            size: 40,
        },
        {
            accessorKey: 'price',
            header: 'Prix',
            size: 60,
            Cell: ({ cell }) => (
                <label>
                    {cell.getValue<number>().toLocaleString('fr-FR', {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2
                    })}
                </label>
            )
        },
        { accessorKey: 'status', header: 'Etat' },
        {
            accessorKey: 'statusCyclade',
            header: 'Cyclade',
            size: 50,
            Cell: ({ row }) => (
                <Tooltip title={`${row.original.statusCyclade}${row.original.msgStatusCyclade ? ' : ' + row.original.msgStatusCyclade : ''}`}>
                    <img alt="" style={{ height: 30 }} src={getSmile(row.original.statusCyclade)} />
                </Tooltip>
            ),
        },
        {
            accessorKey: 'statusElib',
            header: 'Elib',
            size: 50,
            Cell: ({ row }) => (
                <Tooltip title={<div dangerouslySetInnerHTML={{ __html: `${row.original.statusElib}${row.original.msgStatusElib ? " : " + row.original.msgStatusElib : ""}` }} />}>
                    <img alt="" style={{ height: 30 }} src={getSmile(row.original.statusElib)} />
                </Tooltip>
            ),
        }
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: ouvrageList || [],
        enableRowSelection: true,
        enableGlobalFilter: true,
        enableColumnActions: false,
        enableColumnFilters: false,
        positionActionsColumn: 'last',
        localization: MRT_Localization_FR,
        enableStickyHeader: true,
        defaultColumn: {
            minSize: 20,
            size: 50,
        },
        state: {
            isLoading: loading,
            showProgressBars: loading,
        },
        initialState: {
            pagination: { pageSize: 50, pageIndex: 0 },
            showGlobalFilter: true,
        },
        muiTableProps: {
            sx: {
                tableLayout: 'auto',
            },
        },
        muiTableContainerProps: {
            sx: { maxHeight: '47rem', minHeight: '47rem' },
        },
        muiTableBodyCellProps: {
            sx: {
                padding: '6px 8px',
            }
        },
        muiTableBodyRowProps: {
            sx: { fontFamily: "Roboto, Helvetica, Arial, sans-serif" }
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <OuvrageCount />
                
                {/* Affiche les boutons d'action groupés uniquement si des lignes sont sélectionnées */}
                {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
                    <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Réinitialiser les statuts Cyclade">
                            <IconButton onClick={() => refreshList(table.getSelectedRowModel().rows.map(r => r.original))}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Relancer la vérification Cyclade">
                            <IconButton onClick={() => checkList(table.getSelectedRowModel().rows.map(r => r.original))}>
                                <CheckIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Relancer la vérification Elib">
                            <IconButton onClick={() => checkElibList(table.getSelectedRowModel().rows.map(r => r.original))}>
                                <CheckIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        ),
    });

    // 3. Mise à jour de Redux avec le nombre d'éléments filtrés (Remplace onSearchChange)
    useEffect(() => {
        if (!loading && ouvrageList) {
            dispatch(setCount(table.getFilteredRowModel().rows.length));
        }
    }, [loading, ouvrageList, dispatch, table]);

    return (
        <ThemeProvider theme={theme}>
            <MaterialReactTable table={table} />
            
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton />
                <Modal.Body>
                    <img alt="" style={{ width: '100%' }} src={img} />
                </Modal.Body>
            </Modal>
            <OuvrageDetails ref={ouvrageDetailsRef} />
        </ThemeProvider>
    );
};

export default React.memo(OuvrageTable);